import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Separator } from '../components/ui/separator';
import { Switch } from '../components/ui/switch';
import { Progress } from '../components/ui/progress';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../components/ui/alert-dialog';
import {
  Loader2,
  CreditCard,
  Package,
  Check,
  Plus,
  Minus,
  AlertTriangle,
  ExternalLink,
  Calendar,
  Receipt,
  Settings,
  RefreshCw,
  ChevronRight,
  Shield,
  Users,
  Calculator,
  Truck,
  Sparkles,
  Zap,
  X,
} from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Category icons
const categoryIcons = {
  core: Package,
  hr: Users,
  finance: Calculator,
  operations: Truck,
  compliance: Shield,
  integrations: Settings,
  general: Sparkles,
};

// Category colors
const categoryColors = {
  core: 'bg-emerald-100 text-emerald-700',
  hr: 'bg-blue-100 text-blue-700',
  finance: 'bg-amber-100 text-amber-700',
  operations: 'bg-purple-100 text-purple-700',
  compliance: 'bg-red-100 text-red-700',
  integrations: 'bg-cyan-100 text-cyan-700',
  general: 'bg-gray-100 text-gray-700',
};

export default function BillingSubscription() {
  const { token } = useAuth();
  const [loading, setLoading] = useState(true);
  const [subscription, setSubscription] = useState(null);
  const [modules, setModules] = useState([]);
  const [moduleConfig, setModuleConfig] = useState(null);
  const [showCancelDialog, setShowCancelDialog] = useState(false);
  const [showModulesDialog, setShowModulesDialog] = useState(false);
  const [cancelling, setCancelling] = useState(false);
  const [openingPortal, setOpeningPortal] = useState(false);
  const [pendingModule, setPendingModule] = useState(null);
  const [savingModule, setSavingModule] = useState(null);
  const [usage, setUsage] = useState(null);
  const [togglingAddon, setTogglingAddon] = useState(null);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    setLoading(true);
    try {
      const [subRes, modulesRes, configRes, usageRes] = await Promise.all([
        axios.get(`${API_URL}/api/subscriptions/status`, {
          headers: { Authorization: `Bearer ${token}` },
        }),
        axios.get(`${API_URL}/api/modules/available`, {
          headers: { Authorization: `Bearer ${token}` },
        }),
        axios.get(`${API_URL}/api/modules/config`, {
          headers: { Authorization: `Bearer ${token}` },
        }),
        axios.get(`${API_URL}/api/subscriptions/usage`, {
          headers: { Authorization: `Bearer ${token}` },
        }).catch(() => ({ data: null })),
      ]);
      
      setSubscription(subRes.data);
      setModules(modulesRes.data);
      setModuleConfig(configRes.data);
      setUsage(usageRes.data);
    } catch (error) {
      console.error('Error fetching billing data:', error);
      toast.error('Failed to load billing information');
    } finally {
      setLoading(false);
    }
  };

  const handleToggleAddon = async (addonKey, enabled) => {
    setTogglingAddon(addonKey);
    try {
      await axios.put(
        `${API_URL}/api/subscriptions/live-addons`,
        { addon_key: addonKey, enabled },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success(`${enabled ? 'Enabled' : 'Disabled'} successfully`);
      fetchData();
    } catch (e) {
      const d = e.response?.data?.detail;
      if (typeof d === 'object' && d?.code === 'ADDON_UNDER_CONSTRUCTION') {
        toast.info(d.message || 'This add-on is currently Under Construction.');
      } else if (typeof d === 'object' && d?.code === 'ADDON_NOT_AVAILABLE') {
        toast.error(d.message || 'Add-on requires upgrade');
      } else {
        toast.error(typeof d === 'string' ? d : 'Failed to toggle add-on');
      }
    } finally {
      setTogglingAddon(null);
    }
  };

  const handleOpenBillingPortal = async () => {
    setOpeningPortal(true);
    try {
      const response = await axios.post(
        `${API_URL}/api/subscriptions/portal`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      if (response.data.portal_url) {
        window.location.href = response.data.portal_url;
      }
    } catch (error) {
      console.error('Error opening billing portal:', error);
      toast.error('Failed to open billing portal');
      setOpeningPortal(false);
    }
  };

  const handleCancelSubscription = async () => {
    setCancelling(true);
    try {
      await axios.post(
        `${API_URL}/api/subscriptions/cancel`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      toast.success('Subscription cancelled. You will have access until the end of your billing period.');
      setShowCancelDialog(false);
      fetchData();
    } catch (error) {
      console.error('Error cancelling subscription:', error);
      toast.error(error.response?.data?.detail || 'Failed to cancel subscription');
    } finally {
      setCancelling(false);
    }
  };

  const handleReactivateSubscription = async () => {
    try {
      await axios.post(
        `${API_URL}/api/subscriptions/reactivate`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      toast.success('Subscription reactivated!');
      fetchData();
    } catch (error) {
      console.error('Error reactivating subscription:', error);
      toast.error(error.response?.data?.detail || 'Failed to reactivate subscription');
    }
  };

  const isModuleEnabled = (moduleId) => {
    return moduleConfig?.enabled_modules?.some(m => m.module_id === moduleId) || false;
  };

  const handleToggleModule = async (module) => {
    if (module.is_base) {
      toast.error('Base modules cannot be modified');
      return;
    }
    
    setPendingModule({
      ...module,
      action: isModuleEnabled(module.id) ? 'disable' : 'enable'
    });
  };

  const confirmModuleChange = async () => {
    if (!pendingModule) return;
    
    setSavingModule(pendingModule.id);
    
    try {
      const endpoint = pendingModule.action === 'enable' 
        ? '/api/modules/enable' 
        : '/api/modules/disable';
      
      await axios.post(
        `${API_URL}${endpoint}`,
        { module_id: pendingModule.id },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      toast.success(
        pendingModule.action === 'enable' 
          ? `${pendingModule.name} added to your subscription` 
          : `${pendingModule.name} removed from your subscription`
      );
      
      fetchData();
    } catch (error) {
      console.error('Error updating module:', error);
      toast.error(error.response?.data?.detail || 'Failed to update module');
    } finally {
      setSavingModule(null);
      setPendingModule(null);
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return 'N/A';
    return new Date(dateStr).toLocaleDateString('en-US', {
      month: 'long',
      day: 'numeric',
      year: 'numeric'
    });
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'active':
        return 'bg-green-100 text-green-700';
      case 'trialing':
        return 'bg-blue-100 text-blue-700';
      case 'past_due':
        return 'bg-red-100 text-red-700';
      case 'canceled':
        return 'bg-gray-100 text-gray-700';
      default:
        return 'bg-gray-100 text-gray-700';
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="container mx-auto p-6 max-w-4xl space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold flex items-center gap-2">
            <CreditCard className="h-8 w-8 text-primary" />
            Billing & Subscription
          </h1>
          <p className="text-muted-foreground mt-1">
            Manage your subscription, modules, and payment methods
          </p>
        </div>
        <Button variant="outline" onClick={fetchData}>
          <RefreshCw className="mr-2 h-4 w-4" />
          Refresh
        </Button>
      </div>

      {/* Subscription Status Card */}
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <div>
              <CardTitle>Subscription Status</CardTitle>
              <CardDescription>Your current plan and billing cycle</CardDescription>
            </div>
            {subscription?.status && (
              <Badge className={getStatusColor(subscription.status)}>
                {subscription.status === 'trialing' ? 'Free Trial' : subscription.status}
              </Badge>
            )}
          </div>
        </CardHeader>
        <CardContent className="space-y-4">
          {/* Plan Info */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <div>
              <p className="text-sm text-muted-foreground">Current Plan</p>
              <p className="font-semibold capitalize">{subscription?.plan || 'None'}</p>
            </div>
            <div>
              <p className="text-sm text-muted-foreground">Monthly Cost</p>
              <p className="font-semibold">${moduleConfig?.total_monthly_cost?.toFixed(2) || '0.00'}</p>
            </div>
            <div>
              <p className="text-sm text-muted-foreground">Active Modules</p>
              <p className="font-semibold">{moduleConfig?.enabled_modules?.length || 0}</p>
            </div>
            <div>
              <p className="text-sm text-muted-foreground">
                {subscription?.status === 'trialing' ? 'Trial Ends' : 'Next Billing'}
              </p>
              <p className="font-semibold">
                {subscription?.status === 'trialing' 
                  ? formatDate(subscription?.trial_ends_at)
                  : formatDate(subscription?.current_period_end)}
              </p>
            </div>
          </div>

          {/* Warning for cancellation */}
          {subscription?.cancel_at_period_end && (
            <div className="flex items-center gap-3 p-4 bg-amber-50 border border-amber-200 rounded-lg">
              <AlertTriangle className="h-5 w-5 text-amber-600" />
              <div className="flex-1">
                <p className="font-medium text-amber-800">Subscription Ending</p>
                <p className="text-sm text-amber-700">
                  Your subscription will end on {formatDate(subscription?.current_period_end)}.
                  You can reactivate anytime before then.
                </p>
              </div>
              <Button size="sm" onClick={handleReactivateSubscription}>
                Reactivate
              </Button>
            </div>
          )}

          <Separator />

          {/* Actions */}
          <div className="flex flex-wrap gap-3">
            <Button onClick={handleOpenBillingPortal} disabled={openingPortal}>
              {openingPortal ? (
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              ) : (
                <CreditCard className="mr-2 h-4 w-4" />
              )}
              Manage Payment Methods
            </Button>
            
            <Button variant="outline" onClick={handleOpenBillingPortal} disabled={openingPortal}>
              <Receipt className="mr-2 h-4 w-4" />
              View Invoices
            </Button>

            {subscription?.status === 'active' && !subscription?.cancel_at_period_end && (
              <Button 
                variant="outline" 
                className="text-red-600 hover:text-red-700 hover:bg-red-50"
                onClick={() => setShowCancelDialog(true)}
              >
                Cancel Subscription
              </Button>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Usage & Limits Card */}
      {usage && !usage.no_tenant && (
        <Card data-testid="usage-card">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Users className="h-5 w-5" />
              Usage & Limits
            </CardTitle>
            <CardDescription>
              {usage.plan_name} plan · Track how close you are to your limits
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            {/* Employee usage */}
            <div className="space-y-1">
              <div className="flex justify-between text-sm">
                <span>Employees</span>
                <span className="font-medium">
                  {usage.employee_count} / {usage.max_employees === -1 ? '∞' : usage.max_employees}
                </span>
              </div>
              <Progress
                value={usage.max_employees === -1 ? 0 : Math.min(100, (usage.employee_count / usage.max_employees) * 100)}
                className="h-2"
                data-testid="employee-usage-bar"
              />
            </div>
            {/* Client usage */}
            <div className="space-y-1">
              <div className="flex justify-between text-sm">
                <span>Clients</span>
                <span className="font-medium">
                  {usage.client_count} / {usage.max_clients === -1 ? '∞' : usage.max_clients}
                </span>
              </div>
              <Progress
                value={usage.max_clients === -1 ? 0 : Math.min(100, (usage.client_count / usage.max_clients) * 100)}
                className="h-2"
                data-testid="client-usage-bar"
              />
            </div>
          </CardContent>
        </Card>
      )}

      {/* LIVE Add-ons Card */}
      {usage && !usage.no_tenant && (
        <Card data-testid="live-addons-card">
          <CardHeader>
            <div className="flex items-center justify-between">
              <div>
                <CardTitle className="flex items-center gap-2">
                  <Zap className="h-5 w-5 text-amber-500" />
                  LIVE Processing Add-ons
                </CardTitle>
                <CardDescription>
                  Real-time automation for payroll, AP, AR, and POS
                </CardDescription>
              </div>
              {usage.available_addons?.length === 0 && (
                <Badge variant="outline" className="bg-amber-500/10 text-amber-600 border-amber-500/30">
                  Not on this plan
                </Badge>
              )}
            </div>
          </CardHeader>
          <CardContent className="space-y-3">
            {Object.entries(usage.addon_catalog || {}).map(([key, info]) => {
              const isAvailable = usage.available_addons?.includes(key);
              const isEnabled = !!usage.enabled_addons?.[key];
              const isUC = info.under_construction;
              // Phase 12: per-package price lookup
              const planPrice = info.prices?.[usage.plan_id];
              return (
                <div
                  key={key}
                  className={`flex items-center justify-between p-3 rounded-lg border ${isAvailable && !isUC ? '' : 'opacity-70'}`}
                  data-testid={`addon-row-${key}`}
                >
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <p className="font-medium text-sm">{info.name}</p>
                      {isUC ? (
                        <Badge variant="outline" className="text-xs bg-amber-500/10 text-amber-700 border-amber-500/30">
                          Under Construction
                        </Badge>
                      ) : (
                        planPrice != null && (
                          <Badge variant="outline" className="text-xs">+${planPrice}/mo</Badge>
                        )
                      )}
                    </div>
                    <p className="text-xs text-muted-foreground mt-0.5">{info.description}</p>
                  </div>
                  <Switch
                    checked={isEnabled}
                    disabled={!isAvailable || isUC || togglingAddon === key}
                    onCheckedChange={(v) => handleToggleAddon(key, v)}
                    data-testid={`addon-toggle-${key}`}
                  />
                </div>
              );
            })}
            {usage.available_addons?.length === 0 && (
              <p className="text-xs text-center text-muted-foreground pt-2">
                LIVE add-ons are not yet available on your plan.
              </p>
            )}
          </CardContent>
        </Card>
      )}

      {/* Active Modules Card */}
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <div>
              <CardTitle className="flex items-center gap-2">
                <Package className="h-5 w-5" />
                Your Modules
              </CardTitle>
              <CardDescription>
                Features included in your subscription
              </CardDescription>
            </div>
            <Button onClick={() => setShowModulesDialog(true)}>
              <Settings className="mr-2 h-4 w-4" />
              Manage Modules
            </Button>
          </div>
        </CardHeader>
        <CardContent>
          {moduleConfig?.enabled_modules?.length === 0 ? (
            <div className="text-center py-8 text-muted-foreground">
              <Package className="h-12 w-12 mx-auto mb-3 opacity-50" />
              <p>No modules enabled</p>
              <p className="text-sm">Add modules to customize your subscription</p>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              {moduleConfig?.enabled_modules?.map((mod) => {
                const fullModule = modules.find(m => m.id === mod.module_id);
                const CategoryIcon = categoryIcons[fullModule?.category] || Package;
                const colorClass = categoryColors[fullModule?.category] || categoryColors.general;
                
                return (
                  <div 
                    key={mod.module_id}
                    className="flex items-center justify-between p-3 bg-muted/30 rounded-lg"
                  >
                    <div className="flex items-center gap-3">
                      <div className={`p-2 rounded-lg ${colorClass}`}>
                        <CategoryIcon className="h-4 w-4" />
                      </div>
                      <div>
                        <p className="font-medium">{mod.module_name}</p>
                        <p className="text-sm text-muted-foreground">
                          ${mod.price?.toFixed(2)}/month
                        </p>
                      </div>
                    </div>
                    <Badge variant="outline" className="bg-green-50 text-green-700">
                      <Check className="mr-1 h-3 w-3" />
                      Active
                    </Badge>
                  </div>
                );
              })}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Quick Links */}
      <Card>
        <CardContent className="py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <ExternalLink className="h-4 w-4 text-muted-foreground" />
              <span className="text-sm text-muted-foreground">
                Need help? Visit our billing FAQ or contact support
              </span>
            </div>
            <Button variant="ghost" size="sm" onClick={handleOpenBillingPortal}>
              Open Stripe Portal
              <ChevronRight className="ml-1 h-4 w-4" />
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Cancel Subscription Dialog */}
      <AlertDialog open={showCancelDialog} onOpenChange={setShowCancelDialog}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle className="flex items-center gap-2 text-red-600">
              <AlertTriangle className="h-5 w-5" />
              Cancel Subscription?
            </AlertDialogTitle>
            <AlertDialogDescription className="space-y-3">
              <p>Are you sure you want to cancel your subscription?</p>
              <ul className="list-disc list-inside text-sm space-y-1">
                <li>You'll retain access until {formatDate(subscription?.current_period_end)}</li>
                <li>All your data will be preserved</li>
                <li>You can reactivate anytime before the end date</li>
                <li>After the end date, your account will be downgraded</li>
              </ul>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Keep Subscription</AlertDialogCancel>
            <AlertDialogAction
              className="bg-red-600 hover:bg-red-700"
              onClick={handleCancelSubscription}
              disabled={cancelling}
            >
              {cancelling ? (
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              ) : null}
              Yes, Cancel
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Manage Modules Dialog */}
      <Dialog open={showModulesDialog} onOpenChange={setShowModulesDialog}>
        <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Package className="h-5 w-5" />
              Manage Modules
            </DialogTitle>
            <DialogDescription>
              Add or remove modules to customize your subscription
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            {modules.length === 0 ? (
              <div className="text-center py-8 text-muted-foreground">
                <Package className="h-12 w-12 mx-auto mb-3 opacity-50" />
                <p>No modules available</p>
                <p className="text-sm">Contact support to set up your modules</p>
              </div>
            ) : (
              modules.map((module) => {
                const enabled = isModuleEnabled(module.id);
                const CategoryIcon = categoryIcons[module.category] || Package;
                const colorClass = categoryColors[module.category] || categoryColors.general;
                const isSaving = savingModule === module.id;
                
                return (
                  <div 
                    key={module.id}
                    className={`p-4 rounded-lg border ${enabled ? 'bg-primary/5 border-primary/20' : 'bg-muted/30'}`}
                  >
                    <div className="flex items-start justify-between gap-4">
                      <div className="flex items-start gap-3">
                        <div className={`p-2 rounded-lg ${colorClass}`}>
                          <CategoryIcon className="h-5 w-5" />
                        </div>
                        <div>
                          <div className="flex items-center gap-2">
                            <h4 className="font-semibold">{module.name}</h4>
                            {module.is_base && (
                              <Badge variant="secondary" className="text-xs">Required</Badge>
                            )}
                          </div>
                          {module.description && (
                            <p className="text-sm text-muted-foreground mt-1">
                              {module.description}
                            </p>
                          )}
                          {module.features?.length > 0 && (
                            <div className="flex flex-wrap gap-1 mt-2">
                              {module.features.slice(0, 4).map((f) => (
                                <Badge key={f} variant="outline" className="text-xs">
                                  {f.replace(/_/g, ' ')}
                                </Badge>
                              ))}
                              {module.features.length > 4 && (
                                <Badge variant="outline" className="text-xs">
                                  +{module.features.length - 4} more
                                </Badge>
                              )}
                            </div>
                          )}
                        </div>
                      </div>
                      <div className="text-right flex flex-col items-end gap-2">
                        <p className="font-semibold">
                          ${module.price?.toFixed(2)}
                          <span className="text-sm font-normal text-muted-foreground">
                            /{module.interval}
                          </span>
                        </p>
                        {module.is_base ? (
                          <Badge className="bg-green-100 text-green-700">
                            <Check className="mr-1 h-3 w-3" />
                            Included
                          </Badge>
                        ) : (
                          <Button
                            size="sm"
                            variant={enabled ? "outline" : "default"}
                            onClick={() => handleToggleModule(module)}
                            disabled={isSaving}
                          >
                            {isSaving ? (
                              <Loader2 className="h-4 w-4 animate-spin" />
                            ) : enabled ? (
                              <>
                                <Minus className="mr-1 h-4 w-4" />
                                Remove
                              </>
                            ) : (
                              <>
                                <Plus className="mr-1 h-4 w-4" />
                                Add
                              </>
                            )}
                          </Button>
                        )}
                      </div>
                    </div>
                  </div>
                );
              })
            )}
          </div>

          <DialogFooter className="border-t pt-4">
            <div className="flex items-center justify-between w-full">
              <div>
                <p className="text-sm text-muted-foreground">New Monthly Total</p>
                <p className="text-xl font-bold">
                  ${moduleConfig?.total_monthly_cost?.toFixed(2) || '0.00'}
                </p>
              </div>
              <Button onClick={() => setShowModulesDialog(false)}>
                Done
              </Button>
            </div>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Confirm Module Change Dialog */}
      <AlertDialog open={!!pendingModule} onOpenChange={() => setPendingModule(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>
              {pendingModule?.action === 'enable' ? 'Add' : 'Remove'} {pendingModule?.name}?
            </AlertDialogTitle>
            <AlertDialogDescription>
              {pendingModule?.action === 'enable' ? (
                <>
                  This will add <strong>${pendingModule?.price?.toFixed(2)}/month</strong> to your subscription.
                  You'll be charged on your next billing date.
                </>
              ) : (
                <>
                  This will remove <strong>${pendingModule?.price?.toFixed(2)}/month</strong> from your subscription.
                  You'll lose access to features provided by this module.
                </>
              )}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={confirmModuleChange}
              className={pendingModule?.action === 'disable' ? 'bg-red-600 hover:bg-red-700' : ''}
            >
              {pendingModule?.action === 'enable' ? 'Add Module' : 'Remove Module'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
